import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
import { BANG_KEO_PAGE_LIMIT, BANKER, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import {
  CHANGE_BOOKMAKER,
  CHANGE_BOOKMAKER_EXTEND,
} from "@/lib/redux/reducers/actionTypes";
import SportDateTabs from "./SportDateTabs";
import SportFixtureFilter from "./SportFixtureFilter";
import SportMatchRow from "./SportMatchRow";
import { IconCorner, IconLeagueTool } from "./icons";
import "./wp-sport.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

function LeagueGroup({ league, matches }) {
  const [expanded, setExpanded] = useState(true);
  const name = league?.nameVN || league?.name || league?.league_name || "";
  const logo = league?.logo || league?.league_logo || "";

  const toggleExpanded = () => setExpanded((prev) => !prev);

  return (
    <>
      <div
        className="row-league"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggleExpanded}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className="th-league-info">
          <div className="th-league-logo">
            {logo ? <img src={logo} alt={name} loading="lazy" /> : null}
          </div>
          <div className="th-league-name">{name}</div>
        </div>
        <div className="th-group-tools">
          <IconLeagueTool
            width={16}
            height={16}
            className={expanded ? undefined : "collapsed"}
          />
        </div>
      </div>
      {expanded ? (
        <div className="table-body">
          <div className="table-match-box">
            {matches.map((match, mIdx) => (
              <SportMatchRow
                key={
                  match?.id ||
                  `${league?.id}-${mIdx}-${match?.matchTime}`
                }
                match={match}
              />
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

const KbvTiLeV31 = ({ type = "all" }) => {
  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");

  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState("live");
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(20);
  const scrollRootRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);

  const dispatch = useDispatch();
  const bookmarkerExtend = useSelector(
    (state) => state.bookmarkerExtendReducer,
  );
  const bookmarker = useSelector((state) => state.bookmakerReducer);

  const bookmakerOptions = useMemo(
    () => [
      { value: "-1", label: "Tất cả" },
      ...BANKER.map((item) => ({ value: item.value, label: item.name })),
    ],
    [],
  );

  const bookmakerExtendOptions = useMemo(
    () =>
      BANKER_EXTENDS.map((item) => ({
        value: item.value,
        label: `TL ${item.shortName === "DECIMAL" ? "Thập phân" : item.shortName}`,
      })),
    [],
  );

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status =
    type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include:
        "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight,cornerOdd",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    const idSet = new Set(selectedLeagueIds.map(String));
    return baseLeaguesWithMatches.filter(({ league }) =>
      idSet.has(String(league?.id)),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const allFilteredMatches = useMemo(() => {
    return filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
      (matches || []).map((m) => ({ ...m, league })),
    );
  }, [filteredLeaguesWithMatches]);

  const visibleMatches = useMemo(
    () => allFilteredMatches.slice(0, displayLimit),
    [allFilteredMatches, displayLimit],
  );

  const groupedVisibleMatches = useMemo(() => {
    const groups = [];
    visibleMatches.forEach((match) => {
      const leagueId = String(match.league?.id);
      let group = groups.find((g) => String(g.league?.id) === leagueId);
      if (!group) {
        group = { league: match.league, matches: [] };
        groups.push(group);
      }
      group.matches.push(match);
    });
    return groups;
  }, [visibleMatches]);

  const canLoadMore =
    allFilteredMatches.length > displayLimit || hasMore;

  useEffect(() => {
    if (
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  useEffect(() => {
    const root = scrollRootRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel || !canLoadMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (allFilteredMatches.length > displayLimit) {
          setDisplayLimit((prev) => prev + 20);
        } else if (hasMore && !isLoadingMore) {
          handleLoadMore();
        }
      },
      { root, rootMargin: "120px", threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    canLoadMore,
    loading,
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  ]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();
  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  useEffect(() => {
    if (
      firstLoad &&
      selectedDate === "live" &&
      !loading &&
      filteredLeaguesWithMatches.length <= 0
    ) {
      setSelectedDate(todayString);
    }
  }, [
    firstLoad,
    selectedDate,
    loading,
    filteredLeaguesWithMatches,
    todayString,
  ]);

  const handleLeagueFilterChange = (ids) => {
    setSelectedLeagueIds(ids);
    setDisplayLimit(20);
    if (scrollRootRef.current) scrollRootRef.current.scrollTop = 0;
  };

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setSelectedLeagueIds([]);
    setFirstLoad(false);
    setDisplayLimit(20);
    if (scrollRootRef.current) scrollRootRef.current.scrollTop = 0;
  };

  const leagueOptions = useMemo(
    () =>
      baseLeaguesWithMatches.map(({ league, matches }) => ({
        ...league,
        matchCount: (matches || []).length,
      })),
    [baseLeaguesWithMatches],
  );

  return (
    <div className="kbv-v31-root">
      <div className="six-in-one_content">
        <div className="page-odd">
          <SportDateTabs
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
          />
          <SportFixtureFilter
            selectedLeagueIds={selectedLeagueIds}
            onLeagueChange={handleLeagueFilterChange}
            leagueOptions={leagueOptions}
            extendValue={bookmarkerExtend}
            extendOptions={bookmakerExtendOptions}
            onExtendChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
            }
            bookmakerValue={bookmarker}
            bookmakerOptions={bookmakerOptions}
            onBookmakerChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER, payload: val })
            }
          />

          {loading && <Loading />}
          {!loading && error && (
            <NoData message="Lỗi tải dữ liệu trận đấu" />
          )}
          {!loading && (
            <div className="table-scroll" ref={scrollRootRef}>
              <div className="odd-table">
                <div className="row-table row-header row-two-line sticky-header">
                  <div className="th-cell">Thời gian</div>
                  <div className="th-cell">Tỉ số</div>
                  <div className="th-cell th-corner">
                    <IconCorner width={14} height={14} />
                  </div>
                  <div className="th-cell">Đội bóng</div>
                  <div className="th-cell" />
                  <div className="th-cell th-odd-cell">
                    <span>Toàn trận</span>
                    <span>Cược chấp</span>
                    <span>Tài/Xỉu</span>
                    <span>Kèo góc</span>
                    <span>1x2</span>
                  </div>
                </div>

                {groupedVisibleMatches.length === 0 ? (
                  <div style={{ padding: 24 }}>
                    <NoData
                      message={
                        isLoadingMore
                          ? "Đang tìm thêm trận đấu phù hợp..."
                          : "Không tìm thấy trận đấu theo tiêu chí lọc"
                      }
                    />
                  </div>
                ) : (
                  groupedVisibleMatches.map(
                    ({ league, matches }, leagueIndex) => (
                      <div key={league?.id || `league-${leagueIndex}`}>
                        <LeagueGroup
                          league={league || {}}
                          matches={matches}
                        />
                      </div>
                    ),
                  )
                )}

                {canLoadMore ? (
                  <div ref={loadMoreSentinelRef} className="loadmore-box">
                    {isLoadingMore ? (
                      <span className="loading-text">
                        Đang tải trận đấu...
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KbvTiLeV31;
